import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { Music, VolumeX, Play, X, Plus, Trash2 } from "lucide-react";

/**
 * Ambient music for the platform.
 *
 * Each "track" can be:
 *   - { type: "audio", url }   → real .mp3/.ogg from a CC0/CC-BY archive
 *   - { type: "synth" }        → live-generated Tibetan-bowl drone (Web Audio)
 *
 * Members can also paste their OWN audio URL — saved per-browser in
 * localStorage under abs:ambient:custom (an array of {id,name,url}).
 *
 * Browsers block autoplay until first user gesture, so we expose `start()`
 * and re-prompt on subsequent visits if the user previously enabled music.
 */

const STORAGE_KEY = "abs:ambient:enabled";
const VOLUME_KEY = "abs:ambient:volume";
const TRACK_KEY = "abs:ambient:track";
const CUSTOM_KEY = "abs:ambient:custom";

// Curated tracks — verified hot-linked from archive.org public domain uploads
export const BUILT_IN_TRACKS = [
  {
    id: "thai-monks",
    name: "Thai Monks · Healing Mantra",
    description: "Soft chant · 1.8 MB",
    type: "audio",
    url: "https://archive.org/download/22BuddhistMeditationMusicForPositiveEnergyBuddhistThaiMonksChantingHealingMantra/22BuddhistMeditationMusicForPositiveEnergyBuddhistThaiMonksChantingHealingMantra_64kb.mp3",
  },
  {
    id: "dhammachakka",
    name: "Dhammachakka Chant",
    description: "Pāli Wheel-of-Dhamma chant",
    type: "audio",
    url: "https://archive.org/download/Buddhist-Audios/Dammachakka-Chant.mp3",
  },
  {
    id: "namo-tassa",
    name: "Namo Tassa",
    description: "Salutation to the Buddha",
    type: "audio",
    url: "https://archive.org/download/Buddhist-Audios/Namo%20Tassa%20Bagawato%20Arahato%20Samma%20Sam%20Buddha%20Sa.mp3",
  },
  {
    id: "om-mani-padme-hum",
    name: "Om Mani Padme Hum",
    description: "Tibetan compassion mantra",
    type: "audio",
    url: "https://archive.org/download/Buddhist-Audios/OmManiPadmeHumTibetan.wmv.mp3",
  },
  {
    id: "white-tara",
    name: "White Tara Mantra",
    description: "Very relaxing · long",
    type: "audio",
    url: "https://archive.org/download/buddhamantra/%28%E7%99%BD%E5%BA%A6%E6%AF%8D%E5%BF%83%E5%92%92%29%20White%20Tara%20Mantra%20%28Very%20Relaxing%29.mp3",
  },
  {
    id: "migtsema",
    name: "Migtsema · Tsongkhapa",
    description: "Tibetan five-line prayer",
    type: "audio",
    url: "https://archive.org/download/buddhamantra/Migtsema%20Lama%20Tsong%20Khapa.mp3",
  },
  {
    id: "synth-bowls",
    name: "Generated Singing Bowl",
    description: "Live drone · works offline",
    type: "synth",
    freqs: [130.81, 195.99, 261.63],
    lfoRate: 0.18,
    lfoDepth: 1.4,
  },
];

const AmbientCtx = createContext(null);

export function useAmbient() {
  const ctx = useContext(AmbientCtx);
  if (!ctx) throw new Error("useAmbient must be used inside <AmbientMusicProvider>");
  return ctx;
}

export function AmbientMusicProvider({ children }) {
  // Audio HTMLElement for type:"audio" tracks
  const audioRef = useRef(null);
  // WebAudio plumbing for type:"synth" tracks
  const audioCtxRef = useRef(null);
  const synthNodesRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(() => {
    try { return Number(localStorage.getItem(VOLUME_KEY)) || 0.4; } catch { return 0.4; }
  });
  const [trackId, setTrackId] = useState(() => {
    try { return localStorage.getItem(TRACK_KEY) || "thai-monks"; } catch { return "thai-monks"; }
  });
  const [customTracks, setCustomTracks] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CUSTOM_KEY) || "[]"); } catch { return []; }
  });
  const [previouslyEnabled, setPreviouslyEnabled] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });

  // All available tracks = built-in + user-supplied
  const tracks = [...BUILT_IN_TRACKS, ...customTracks.map((c) => ({ ...c, type: "audio", custom: true }))];
  const currentTrack = tracks.find((t) => t.id === trackId) || tracks[0];

  const stopSynth = useCallback(() => {
    const c = audioCtxRef.current;
    const n = synthNodesRef.current;
    if (!c || !n) return;
    try {
      const t = c.currentTime;
      n.masterGain.gain.cancelScheduledValues(t);
      n.masterGain.gain.setValueAtTime(n.masterGain.gain.value, t);
      n.masterGain.gain.linearRampToValueAtTime(0.0001, t + 0.4);
      setTimeout(() => {
        try { n.oscs.forEach((o) => o.stop()); n.lfo.stop(); } catch (e) { /* ignore */ }
        synthNodesRef.current = null;
      }, 500);
    } catch (e) { /* ignore */ }
  }, []);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch (e) { /* ignore */ }
    }
  }, []);

  const stop = useCallback(() => {
    stopSynth();
    stopAudio();
    setPlaying(false);
  }, [stopAudio, stopSynth]);

  const startSynth = useCallback((track) => {
    try {
      if (!audioCtxRef.current) {
        // eslint-disable-next-line no-undef
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const c = audioCtxRef.current;
      if (c.state === "suspended") c.resume().catch(() => {});

      stopSynth();

      const masterGain = c.createGain();
      masterGain.gain.value = 0.0001;
      masterGain.connect(c.destination);

      const lfo = c.createOscillator();
      lfo.frequency.value = track.lfoRate || 0.15;
      const lfoGain = c.createGain();
      lfoGain.gain.value = (track.lfoDepth || 1.4) * 0.01;
      lfo.connect(lfoGain);

      const oscs = (track.freqs || [130.81, 195.99, 261.63]).map((freq, i) => {
        const o = c.createOscillator();
        o.type = i === 0 ? "sine" : (i === 1 ? "sine" : "triangle");
        o.frequency.value = freq;
        o.detune.value = (i - 1) * 6;
        const g = c.createGain();
        g.gain.value = i === 0 ? 1.0 : (i === 1 ? 0.55 : 0.35);
        lfoGain.connect(g.gain);
        o.connect(g);
        g.connect(masterGain);
        o.start();
        return o;
      });
      lfo.start();

      const t0 = c.currentTime;
      masterGain.gain.cancelScheduledValues(t0);
      masterGain.gain.setValueAtTime(0.0001, t0);
      masterGain.gain.exponentialRampToValueAtTime(Math.max(0.001, volume * 0.5), t0 + 1.5);

      synthNodesRef.current = { masterGain, oscs, lfo };
    } catch (e) {
      setPlaying(false);
    }
  }, [stopSynth, volume]);

  const startAudio = useCallback((track) => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.loop = true;
      audioRef.current.crossOrigin = "anonymous";
    }
    audioRef.current.src = track.url;
    audioRef.current.volume = volume;
    audioRef.current.play().catch((err) => {
      // Autoplay rejection or network/CORS error
      console.warn("Ambient music play blocked:", err?.message);
      setPlaying(false);
    });
  }, [volume]);

  const start = useCallback(() => {
    const track = currentTrack;
    if (!track) return;
    stopSynth();
    stopAudio();
    if (track.type === "synth") startSynth(track);
    else startAudio(track);
    setPlaying(true);
    setPreviouslyEnabled(true);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
  }, [currentTrack, startAudio, startSynth, stopAudio, stopSynth]);

  const toggle = useCallback(() => {
    if (playing) {
      stop();
      try { localStorage.setItem(STORAGE_KEY, "0"); } catch { /* ignore */ }
      setPreviouslyEnabled(false);
    } else {
      start();
    }
  }, [playing, start, stop]);

  // Restart on track change while playing
  useEffect(() => {
    try { localStorage.setItem(TRACK_KEY, trackId); } catch { /* ignore */ }
    if (playing) setTimeout(() => start(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackId]);

  // Apply volume changes live
  useEffect(() => {
    try { localStorage.setItem(VOLUME_KEY, String(volume)); } catch { /* ignore */ }
    if (audioRef.current) audioRef.current.volume = volume;
    const n = synthNodesRef.current;
    const c = audioCtxRef.current;
    if (n && c) {
      const t = c.currentTime;
      n.masterGain.gain.cancelScheduledValues(t);
      n.masterGain.gain.setValueAtTime(n.masterGain.gain.value, t);
      n.masterGain.gain.linearRampToValueAtTime(Math.max(0.0001, volume * 0.5), t + 0.2);
    }
  }, [volume]);

  // Persist custom tracks
  useEffect(() => {
    try { localStorage.setItem(CUSTOM_KEY, JSON.stringify(customTracks)); } catch { /* ignore */ }
  }, [customTracks]);

  const addCustomTrack = useCallback((name, url) => {
    const id = `custom-${Date.now().toString(36)}`;
    setCustomTracks((prev) => [...prev, { id, name: name.trim() || "My track", url: url.trim() }]);
    return id;
  }, []);

  const removeCustomTrack = useCallback((id) => {
    setCustomTracks((prev) => prev.filter((t) => t.id !== id));
    if (trackId === id) setTrackId(BUILT_IN_TRACKS[0].id);
  }, [trackId]);

  // Self-healing: if our `playing` flag is still true but the underlying
  // <audio> element gets paused by the browser (e.g. iOS Safari steals focus
  // when Jitsi joins), poll briefly and re-trigger play. Without this, music
  // mysteriously dies as soon as a member enters a meeting.
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      const a = audioRef.current;
      if (a && a.paused && currentTrack?.type === "audio") {
        a.play().catch(() => { /* still blocked, try again next tick */ });
      }
    }, 4000);
    return () => clearInterval(id);
  }, [playing, currentTrack?.type]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try {
        stopAudio();
        stopSynth();
        if (audioCtxRef.current?.close) audioCtxRef.current.close().catch(() => {});
      } catch (e) { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    playing, volume, setVolume,
    trackId, setTrackId,
    tracks, currentTrack,
    customTracks, addCustomTrack, removeCustomTrack,
    start, stop, toggle, previouslyEnabled,
  };

  return (
    <AmbientCtx.Provider value={value}>
      {children}
      <AmbientResumePrompt />
    </AmbientCtx.Provider>
  );
}

function AmbientResumePrompt() {
  const { playing, previouslyEnabled, start } = useAmbient();
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);
  if (!previouslyEnabled || playing || dismissed || !visible) return null;
  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 bg-navy text-pearl rounded-full shadow-2xl border border-white/15"
      data-testid="ambient-resume-prompt"
    >
      <Music size={14} className="text-saffron" />
      <span className="text-sm">Resume ambient music?</span>
      <button
        onClick={() => { start(); setDismissed(true); }}
        data-testid="ambient-resume-yes"
        className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-saffron text-navy font-medium hover:bg-saffron/90"
      >
        <Play size={11} /> Yes
      </button>
      <button onClick={() => setDismissed(true)} aria-label="Dismiss" className="text-pearl/60 hover:text-pearl">
        <X size={14} />
      </button>
    </div>
  );
}

/**
 * Compact controls for the sidebar — popover with volume slider, track picker
 * and a "Add your own track" form.
 */
export function AmbientControls({ className = "" }) {
  const {
    playing, toggle, volume, setVolume,
    trackId, setTrackId, tracks,
    customTracks, addCustomTrack, removeCustomTrack,
  } = useAmbient();
  const [open, setOpen] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newUrl, setNewUrl] = useState("");

  const submitCustom = (e) => {
    e.preventDefault();
    const url = newUrl.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      alert("Please paste a full URL starting with http:// or https://");
      return;
    }
    const id = addCustomTrack(newName, url);
    setNewName(""); setNewUrl(""); setShowAdd(false);
    setTrackId(id);
  };

  return (
    <div className={`relative ${className}`} data-testid="ambient-controls">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="ambient-controls-toggle"
        className={`flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm border transition-colors ${
          playing
            ? "bg-saffron/15 border-saffron/40 text-saffron"
            : "bg-white/5 border-white/15 text-pearl/70 hover:border-saffron/40"
        }`}
        title={playing ? "Ambient music · playing" : "Ambient music · off"}
      >
        {playing ? <Music size={14} /> : <VolumeX size={14} />}
        <span className="text-xs flex-1 text-left truncate">
          {playing ? "Ambient music" : "Ambient · off"}
        </span>
      </button>

      {open && (
        <div
          className="absolute bottom-full left-0 mb-2 w-72 bg-[#0B1F4D] border border-white/15 rounded-lg shadow-2xl p-4 z-40 text-pearl max-h-[28rem] overflow-y-auto"
          data-testid="ambient-controls-panel"
        >
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">Ambient music</p>
            <button onClick={() => setOpen(false)} className="text-pearl/50 hover:text-pearl"><X size={14} /></button>
          </div>

          <button
            onClick={toggle}
            data-testid="ambient-play-toggle"
            className={`mt-3 w-full px-3 py-2 rounded-md text-xs font-medium border transition-colors ${
              playing
                ? "bg-red-600/85 border-red-500 text-white hover:bg-red-600"
                : "bg-saffron text-navy border-saffron hover:bg-saffron/90"
            }`}
          >
            {playing ? "Pause" : "Play"}
          </button>

          <div className="mt-4">
            <label className="text-[10px] uppercase tracking-widest text-saffron/85">Volume</label>
            <input
              type="range" min="0" max="1" step="0.01"
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              data-testid="ambient-volume"
              className="w-full mt-1.5 accent-saffron"
            />
          </div>

          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-widest text-saffron/85 mb-1.5">Tracks</p>
            <div className="space-y-1">
              {tracks.map((t) => (
                <div
                  key={t.id}
                  className={`flex items-center group px-2 py-1.5 rounded-md text-xs transition-colors ${
                    trackId === t.id ? "bg-saffron/20 text-saffron" : "text-pearl/75 hover:bg-white/5"
                  }`}
                >
                  <button
                    onClick={() => setTrackId(t.id)}
                    data-testid={`ambient-track-${t.id}`}
                    className="flex-1 text-left min-w-0"
                  >
                    <div className="font-medium truncate">{t.name}</div>
                    <div className="text-[10px] text-pearl/45 truncate">{t.description || (t.custom ? "Custom" : "")}</div>
                  </button>
                  {t.custom && (
                    <button
                      onClick={(e) => { e.stopPropagation(); removeCustomTrack(t.id); }}
                      title="Remove this custom track"
                      data-testid={`ambient-remove-${t.id}`}
                      className="ml-2 p-1 text-pearl/40 hover:text-red-400 opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-white/10">
            {!showAdd ? (
              <button
                onClick={() => setShowAdd(true)}
                data-testid="ambient-add-toggle"
                className="w-full inline-flex items-center justify-center gap-1.5 text-xs text-pearl/60 hover:text-saffron py-1.5"
              >
                <Plus size={12} /> Add your own track URL
              </button>
            ) : (
              <form onSubmit={submitCustom} className="space-y-2" data-testid="ambient-add-form">
                <p className="text-[10px] uppercase tracking-widest text-saffron/85">Add a track</p>
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Track name"
                  data-testid="ambient-new-name"
                  className="w-full px-2 py-1.5 rounded-md bg-white/5 border border-white/15 text-xs text-pearl placeholder-pearl/30 focus:outline-none focus:border-saffron"
                  maxLength={60}
                />
                <input
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="https://…/track.mp3"
                  data-testid="ambient-new-url"
                  className="w-full px-2 py-1.5 rounded-md bg-white/5 border border-white/15 text-xs text-pearl placeholder-pearl/30 focus:outline-none focus:border-saffron"
                  required
                />
                <p className="text-[10px] text-pearl/45 leading-snug">
                  Direct .mp3 / .ogg URL only. The host server must allow cross-origin access.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowAdd(false); setNewName(""); setNewUrl(""); }}
                    className="flex-1 text-xs text-pearl/60 hover:text-pearl py-1.5"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    data-testid="ambient-add-submit"
                    className="flex-1 text-xs bg-saffron text-navy rounded-md py-1.5 font-medium hover:bg-saffron/90"
                  >
                    Add
                  </button>
                </div>
              </form>
            )}
          </div>

          {customTracks.length > 0 && (
            <p className="mt-3 text-[9px] text-pearl/40 italic leading-snug">
              Your custom tracks are saved on this device only.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
